import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as InfoWarningIcon } from '@/assets/icons/info-warning.svg';
import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerHolidayRequestResource } from '@/types/resources';
import { api } from '@/utils/api';

const Dialog = styled(StyledDialog)`
  width: 100%;
  max-width: 598px;
  padding: 55px 24px 0;
`;

const Title = styled(Text)`
  margin-bottom: 24px;
`;

const ModalContent = styled.div`
  padding-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoBox = styled.div<{ $isGray?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding: 16px;
  border-radius: 4px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  text-align: left;
  ${({ $isGray }) =>
    $isGray &&
    css`
      background-color: ${({ theme }) => theme.colors.grayscaleSuperExtraLight};
    `}
`;

const Label = styled(Text)`
  margin-bottom: 4px;
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.purple};
`;

const DaysText = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ImportantBox = styled.div`
  padding: 12px 16px;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 24px;
  padding: 24px;
  padding-bottom: 32px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  Button {
    flex: 1;
  }
  margin-left: -24px;
  width: calc(100% + 48px);
`;

const ExeecingText = styled(Text)`
  svg {
    display: inline-block;
    margin-right: 8px;
    transform: translateY(3px);
  }
`;

interface Props {
  request: FreelancerHolidayRequestResource;
  onClose: () => void;
}

const VacationApprovalModal: FC<Props> = ({ request, onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const isHrUser = useHasPermission('manage freelancers');

  const availableDays = request.holidayBalance?.availableDays;
  const availableDaysText = availableDays
    ? `${availableDays} ${availableDays === 1 ? 'day' : 'days'}`
    : '-';
  const deductedDaysText = `${request.totalDays} ${request.totalDays === 1 ? 'day' : 'days'}`;
  const vacationPeriod = `${request.startDate ? DateTime.fromISO(request.startDate).toFormat('yyyy/LL/dd') : '-'} - ${request.endDate ? DateTime.fromISO(request.endDate).toFormat('yyyy/LL/dd') : '-'}`;

  const handleApprove = async () => {
    const confirmed = await alerts.confirm('Are You sure You want to confirm?');
    if (!confirmed) {
      return;
    }
    try {
      await api.post(
        `/v1/payments/freelancers/holidays/requests/${request.id}/approve`,
        {
          ...(isHrUser
            ? { approve_type: 'hr' }
            : { approve_type: 'team_lead' }),
        },
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      alerts.success('Vacation request approved successfully');
      onClose();
    } catch (e) {
      alerts.error('Failed to approve vacation request');
    }
  };

  const handleDecline = async () => {
    const confirmed = await alerts.confirm('Are You sure You want to decline?');
    if (!confirmed) {
      return;
    }
    try {
      await api.post(
        `/v1/payments/freelancers/holidays/requests/${request.id}/reject`,
        {
          ...(isHrUser
            ? { decline_type: 'hr' }
            : { decline_type: 'team_lead' }),
        },
      );
      await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
        exact: false,
      });
      alerts.success('Vacation request declined successfully');
      onClose();
    } catch (e) {
      alerts.error('Failed to decline vacation request');
    }
  };

  return (
    <Modal>
      <Dialog>
        <Title $type="heading2">Approve Vacation</Title>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <ModalContent>
          <InfoBox $isGray={true}>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Name
              </Label>
              <Text $type="captionSemibold">{request.freelancerName}</Text>
            </div>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Request Date
              </Label>
              <Text $type="captionSemibold">
                {DateTime.fromISO(request.requestedAt).toFormat('yyyy/LL/dd')}
              </Text>
            </div>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Available Days
              </Label>
              <Text $type="captionSemibold">{availableDaysText}</Text>
            </div>
          </InfoBox>
          <InfoBox>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Vacation Date
              </Label>
              <Text $type="captionSemibold">{vacationPeriod}</Text>
            </div>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Deducted Days
              </Label>
              <DaysText $type="captionSemibold">
                <Dot />
                {deductedDaysText}
              </DaysText>
            </div>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Comment
              </Label>
              <Text $type="captionSemibold">{request.notes || '-'}</Text>
            </div>
            {request.exceedsCurrentBalance && (
              <div>
                <Label $type="smallHeadline" $color="gray">
                  Warning
                </Label>
                <ExeecingText $type="captionSemibold">
                  <InfoWarningIcon />
                  Request exeeds freelancer's current vacation balance.
                </ExeecingText>
              </div>
            )}
          </InfoBox>
          <ImportantBox>
            <InfoWarningIcon />
            <Text $type="smallBold">
              If you decline a vacation request, please make sure to contact the
              freelancer.
            </Text>
          </ImportantBox>
        </ModalContent>
        <ModalFooter>
          <Button onClick={handleApprove} type="button">
            Approve
          </Button>
          <Button color="reset" type="button" onClick={handleDecline}>
            Decline
          </Button>
        </ModalFooter>
      </Dialog>
    </Modal>
  );
};

export default VacationApprovalModal;
